/**
 * agentComposition — composite-agent grouping for the Agents list.
 *
 * A "wrapper" agent's graph dispatches OTHER deployed agents as sub-workflows;
 * the backend list endpoint surfaces those child marketplace slugs on each row
 * as `composedOf: [slug, ...]` (stamped at deploy from the graph's static
 * `dispatchesWorkflow` node markers; see backend workflow-composition.js).
 *
 * groupAgents() applies the LIST-presentation rules:
 *   - A row whose slug (workflowType OR marketplaceSlug) appears in ANY OTHER
 *     row's member set is WRAPPED → hidden from the top level. This applies
 *     recursively for free: a hidden wrapper's own composedOf still hides ITS
 *     members (membership is computed over ALL rows, not just visible ones).
 *   - The wrapper row gets `members: [row, ...]` — the DEPLOYED member rows
 *     resolved in dispatch order, so the UI can render their icons as a strip.
 *   - Dangling reference (member slug with no deployed row) → skipped, never
 *     crashes. Two wrappers sharing a member → member hidden once, resolved
 *     into BOTH strips. Self-reference → ignored (a row never hides itself).
 *
 * Pure + non-mutating: input rows are never modified; wrapper rows are shallow
 * -copied to attach `members`. Detail pages / deep links are unaffected — this
 * only shapes the LIST.
 */

const rowMatchesSlug = (row, slug) =>
  !!row && (row.workflowType === slug || row.marketplaceSlug === slug);

export function groupAgents(rows) {
  const list = Array.isArray(rows) ? rows.filter((r) => r && typeof r === 'object') : [];

  // Effective member slugs per row = composedOf minus self-references.
  const memberSlugsOf = (row) => {
    const slugs = Array.isArray(row.composedOf) ? row.composedOf : [];
    return slugs.filter((s) => typeof s === 'string' && s && !rowMatchesSlug(row, s));
  };

  // Wrapped = referenced by ANY OTHER row (visible or itself hidden — that's
  // what makes nesting recursive).
  const wrapped = new Set();
  for (const row of list) {
    for (const slug of memberSlugsOf(row)) {
      for (const other of list) {
        if (other !== row && rowMatchesSlug(other, slug)) wrapped.add(other);
      }
    }
  }

  return list
    .filter((row) => !wrapped.has(row))
    .map((row) => {
      const members = memberSlugsOf(row)
        .map((slug) => list.find((r) => r !== row && rowMatchesSlug(r, slug)))
        .filter(Boolean);
      return members.length ? { ...row, members } : row;
    });
}

export default groupAgents;
